import { useSelector } from "react-redux";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { useTranslation } from "react-i18next";
import DashboardPagesTemplate from "../../Template/DashboardPagesTemplate";
import DashboardPageTitle from "../../Components/InnerPageTitle";
import { Badge, Button, Form, InputGroup, Modal, Table } from "react-bootstrap";
import "../../Assets/scss/pages/website-list.scss";
import React, { useEffect, useRef, useState } from "react";
import useWindowScroll from "../../Helpers/useWindowScroll";
import NoRecordFound from "../../Components/NoRecordFound";
import ShowRecordItem from "../../Components/ShowRecordItem";
import PaginationComponent from "../../Components/Pagination";
import { useDispatch } from "react-redux";
import { UserActivityPropTypes } from "../../PropTypes/UserPropTypes";
import parseISODate from "../../Helpers/parseISODate";
import { getAdminSubUserListService } from "../../Services/AdminManagementService";
import { LOADING, SET_MESSAGE } from "../../Actions/Types";
import { getAllPermission } from "../../Api/UserManagementAPI";
import { addAdminSubUser, deleteAdminSubUser, getAdminPermissions, getAdminSubUsers, saveAdminUserPermissions, updateAdminSubUser } from "../../Api/AdminManagementAPI";

type User = {
  id: number;
  name: string;
  email: string;
  password?: string;
  permissions?: number[];
};

interface Menu {
  id: number;
  name: string;
  title: string;
  guard_name: string;
  created_at: string;
  updated_at: string;
}

interface FormError {
  name?: string;
  email?: string;
  websites?: string;
}

// component to display user activity
const AdminManagementPage = () => {

    const [totalRecord, setTotalRecord] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [limit, setLimit] = useState<number>(10);
    const [offset, setOffset] = useState<number>(0);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [showAccessModal, setShowAccessModal] = useState(false);
    const prevLimitRef = useRef<number>(10);
    const [userList, setUserList] = useState<UserActivityPropTypes[]>([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);
    const [allPermission, setAllPermission] = useState<Menu[]>([]);
    const [error, setError] = useState<FormError>({});
    const [showUserModal, setShowUserModal] = useState(false);
    const [checkedMenus, setCheckedMenus] = useState<number[]>([]);
    const [showAllWebsitesModal, setShowAllWebsitesModal] = useState(false);
    const [form, setForm] = useState<any>({
      name: "",
      email: "",
      // password: "",
      // confirmPassword: "",
    });
    // Redux Data
    const { currentWebsite, user, accessToken } = useSelector((store: StoreProptypes) => store);

    const dispatch = useDispatch();

    const windowScrollValue = useWindowScroll('aioa_dashboard-website-list-filter');

    // Functions
    const { t } = useTranslation();

    useEffect(() => {
      setCurrentPage(1);
    }, [limit]);
  
    useEffect(() => {
      setOffset(currentPage * limit - limit);
    }, [currentPage, limit]);
  
    useEffect(() => {
      // check if access token is available, limit and domain search is not changed
      if (accessToken && ((limit === prevLimitRef.current))) {
        // service to get user activity list with filters
        getAdminSubUserListService(accessToken, dispatch, t, setUserList, limit, offset, setTotalRecord, currentPage);
      }
    }, [offset, user]);
  
    useEffect(() => {
      // check if limit is changed, or domain search is changed; and offset is set to 0
      if ((limit !== prevLimitRef.current)) {
        // check if access token exists
        if (accessToken) {
          // service to get user activity list with filters
          getAdminSubUserListService(accessToken, dispatch, t,setUserList, limit, offset, setTotalRecord, currentPage);
        }
  
        prevLimitRef.current = limit;
      }
    }, [offset, limit]);

    

    useEffect(() => {
        const getAllPermissionData = async () => {
          try {
            const res = await getAllPermission(accessToken || "");
            if (res?.status === "success") {
              console.log(res?.data.pop());
              
              setAllPermission(res?.data || []);
            }
          } catch (err: any) {
            console.error("Error fetching users:", err.message);
          }
        };
        getAllPermissionData();
    }, [accessToken]);

    const handleDeleteClick = (user: User) => {
      setUserToDelete(user);
      setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!userToDelete) return;
        try {
        dispatch({ type: LOADING, payload: true });

        await deleteAdminSubUser(accessToken || "", userToDelete.id);
        if (accessToken) {
          await getAdminSubUserListService(accessToken, dispatch, t,setUserList, limit, offset, setTotalRecord, currentPage);
        }

        dispatch({
            type: SET_MESSAGE,
            payload: {
            toast_message: t("User deleted successfully!"),
            toast_type: "success",
            },
        });
        } catch (err: any) {
        dispatch({
            type: SET_MESSAGE,
            payload: {
            toast_message: err.message || t("Failed to delete user"),
            toast_type: "danger",
            },
        });
        } finally {
        dispatch({ type: LOADING, payload: false });
        setShowDeleteModal(false);
        setUserToDelete(null);
        }
    };

    const handleShowUserModal = (user?: User) => {
      if (user) {
        setEditingUser(user);
        setForm({
          name: user.name,
          email: user.email,
          // password: "",
          // confirmPassword: "",
        });
      } else {
        setEditingUser(null);
        setForm({
          name: "",
          email: "",
          // password: "",
          // confirmPassword: "",
        });
        setCheckedMenus([])
      }
      setError({});
      setShowUserModal(true);
    };

    const validateForm = () => {
      const newErrors: FormError = {};

      if (!form.name || form.name.trim() === "") {
        newErrors.name = t("Full name is required.");
      } else if (!/^[A-Za-z\s]{1,50}$/.test(form.name)) {
        newErrors.name = t("Name should not contain numbers or special characters.");
      }

      if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        newErrors.email = t("Enter a valid email address.");
      }

      setError(newErrors);
      return Object.keys(newErrors).length === 0; // ✅ true = valid form
    };

    const handleSaveUser = async () => {
    if (!validateForm()) return;

    dispatch({ type: LOADING, payload: true });

    try {
      // 1️⃣ Prepare base user payload
      const userPayload: any = {
        name: form.name,
        email: form.email,
      };

      // 2️⃣ Add or Update user
      let userResponse;
      if (editingUser) {
        userResponse = await updateAdminSubUser(
          accessToken || "",
          editingUser.id,
          userPayload
        );
      } else {
        userResponse = await addAdminSubUser(accessToken || "", userPayload);
      }

      // 3️⃣ Stop if add/update failed
      if (userResponse?.status !== 200 && userResponse?.status !== "success") {
        const errorMsg =
          userResponse?.errors?.email?.[0] || t("Failed to save user");
        setError(errorMsg);
        dispatch({
          type: SET_MESSAGE,
          payload: { toast_message: errorMsg, toast_type: "danger" },
        });
        return;
      }

      const newUserId = userResponse?.Data?.id || editingUser?.id;

      // 6️⃣ Save permissions for user
      if (!editingUser) {
        const permissionsPayload = {
          user_id: newUserId,
          permissions: checkedMenus,
        };

        const savePermRes = await saveAdminUserPermissions(
          accessToken || "",
          permissionsPayload
        );

        if (savePermRes?.status !== "success")
          throw new Error("Failed to save user permissions");
      }
      // 7️⃣ Refresh user list
      if (accessToken) {
        await getAdminSubUserListService(accessToken, dispatch, t,setUserList, limit, offset, setTotalRecord, currentPage);
      }
      setShowUserModal(false);

      // 8️⃣ Success toast
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: editingUser
            ? t("Sub-user updated successfully!")
            : t(
                `The sub-user account has been created successfully. A password reset link has been sent to the sub-user’s email address.`
              ),
          toast_type: "success",
        },
      });
    } catch (err: any) {
      console.error("Error saving sub-user:", err);
      const message =
        err.response?.data?.message || err.message || t("Failed to save user");
      setError(message);
      dispatch({
        type: SET_MESSAGE,
        payload: { toast_message: message, toast_type: "danger" },
      });
    } finally {
      dispatch({ type: LOADING, payload: false });
    }
    };

    const handleOpenAccessModal = async (user: User) => {
        if (!user) return;
    
        await setEditingUser(user);
    
        if (
          accessToken &&
          user?.id &&
          user?.permissions &&
          user?.permissions.length > 0
        ) {
          try {
            setCheckedMenus(user?.permissions)
            await getAdminPermissions(
              accessToken || "",
              user?.id
            )
          } catch (err) {
            console.error("Error fetching permissions:", err);
          }
        } else {
          // New user → default: all websites with all permissions
          setCheckedMenus([])
        }
        setShowAccessModal(true);
    };

    const resetForm = async() => {

    }

    const handleSaveAccess = async () => {
        if (!editingUser) return;
    
        // ❌ Validation: At least one website must be selected
        if (checkedMenus.length === 0) {
          dispatch({
            type: SET_MESSAGE,
            payload: {
              toast_message: t("Please select at least one permissions."),
              toast_type: "danger",
            },
          });
          return;
        }
    
        dispatch({ type: LOADING, payload: true });
    
        try {
          // Build payload for API
          const payload = {
            user_id: editingUser.id,
            permissions: checkedMenus,
          };
    
          const response = await saveAdminUserPermissions(accessToken || "", payload);
          if (response?.status === "success") {
            if (accessToken) {
              await getAdminSubUserListService(accessToken, dispatch, t,setUserList, limit, offset, setTotalRecord, currentPage);
            }
            setShowAccessModal(false);
    
            dispatch({
              type: SET_MESSAGE,
              payload: {
                toast_message: t("Permissions saved successfully!"),
                toast_type: "success",
              },
            });
          } else {
            throw new Error(response.message || "Failed to save permissions");
          }
        } catch (err: any) {
          console.error("Failed to save permissions:", err);
          dispatch({
            type: SET_MESSAGE,
            payload: {
              toast_message: err.message || "Failed to save permissions",
              toast_type: "danger",
            },
          });
        } finally {
          dispatch({ type: LOADING, payload: false });
        }
      };


  return (
    <>
      <DashboardPagesTemplate>

        <DashboardPageTitle
          pageTitle={`${t("Admin Management")}`}
          subPageTitle={`${t("Manage users and their sidebar access")}`}
        />

        <div className="mt-3 d-flex justify-content-between align-items-center">
          
        </div>
        <div className="aioa_dashboard-website-list">
            <div className={`aioa_dashboard-website-list-filter-wrapper ${windowScrollValue}`} id="aioa_dashboard-website-list-filter">

            <div className="d-flex justify-content-between align-items-center w-100 px-4">
              <div className="w-50">
                <Button variant="primary" onClick={() => handleShowUserModal()}>
                  {t("+ Add User")}
                </Button>
              </div>
              <div className="aioa_dashboard-website-list-filter-block">
                <Form.Label htmlFor='showPages'>{t("Items Per Page")}</Form.Label>
                <Form.Select
                  onChange={(e) => setLimit(parseInt(e.target.value))}
                  id="showPages"
                  value={limit}
                  aria-label={t("Items Per Page").toString()}
                >
                  <option value="10">{t("10")}</option>
                  <option value="20">{t("20")}</option>
                  <option value="40">{t("40")}</option>
                  <option value="80">{t("80")}</option>
                  <option value="100">{t("100")}</option>
                </Form.Select>
              </div>
            </div>

            </div>

            <div className="aioa_dashboard-table">

            <Table className="mb-0 align-middle user-management-table">
              <thead className="bg-light">
                <tr>
                  <th>{t("Full Name")}</th>
                  <th>{t("Email")}</th>
                  <th>{t("Access")}</th>
                  <th className="text-center" style={{ width: "300px" }}>
                    {t("Actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                  {userList.map((item: any) => (
                    <tr key={item.id}>
                      <td>
                        <strong>{item?.name}</strong>
                      </td>
                      <td className="text-muted">{item?.email}</td>
                      <td>
                        {item?.permissions && item?.permissions !== null && item?.permissions?.length > 0 ? (
                          <>
                            {item?.permissions
                              ?.slice(0, 2)
                              .map((w: any, idx: number) => (
                                <Badge
                                  bg="light"
                                  text="dark"
                                  className="me-1 border"
                                  key={idx}
                                >
                                  {allPermission.find((d:any) => d.id === w)?.name}
                                </Badge>
                              ))}
                            {item?.permissions && item?.permissions !== null && item?.permissions?.length > 2 && (
                              <Badge
                                bg="primary"
                                className="me-1"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  if (item?.permissions && item?.permissions) {
                                    setCheckedMenus(item?.permissions);
                                  }
                                  setShowAllWebsitesModal(true);
                                }}
                              >
                                +{item?.permissions && item?.permissions?.length - 2}
                              </Badge>
                            )}
                          </>
                        ) : (
                          <span className="text-muted">{t("No access")}</span>
                        )}
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-light text-primary"
                          size="sm"
                          className="me-2 action-btn"
                          title="Access"
                          onClick={async () => {
                            await handleOpenAccessModal(item);
                          }}
                        >
                          <i className="material-symbols-outlined">{t("settings")}</i>
                        </Button>
                        <Button
                          variant="outline-light text-danger"
                          size="sm"
                          className="action-btn"
                          title="Delete"
                          onClick={() => handleDeleteClick(item)}
                        >
                          <i className="material-symbols-outlined">{t("delete")}</i>
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>

            <div className="aioa_dashboard-table-pagination-main">
                <>
                <ShowRecordItem
                    offset={offset}
                    limit={limit}
                    totalRecord={totalRecord}
                />
                <PaginationComponent
                    currentPage={currentPage}
                    totalRecords={totalRecord}
                    itemsPerPage={limit}
                    setCurrentPage={setCurrentPage}
                />
                </>
            </div>

            </div>

        </div>
      </DashboardPagesTemplate>
      {/* Add/Edit User Modal */}
      <Modal
        show={showUserModal}
        size="lg"
        onHide={() => setShowUserModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{editingUser ? t(`Edit User`) : t(`Add User`)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Label>{t("Full Name")}</Form.Label>
                <Form.Control
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                {error?.name && (
                  <small className="text-danger">{error?.name}</small>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <Form.Label>{t("Email")}</Form.Label>
                <Form.Control
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  disabled={!!editingUser}
                />
                {error?.email && (
                  <small className="text-danger">{error?.email}</small>
                )}
              </div>
            </div>

            {!editingUser && (
              <>
                <div
                  style={{
                    maxHeight: "330px", // Adjust height as needed
                    overflowY: "auto",
                    padding: "8px",
                  }}
                >
                  {/* Individual Permissions */}
                {allPermission &&
                  allPermission.map((menu) => {
                    const isChecked = checkedMenus.includes(menu.id);

                    return (
                      <div
                        key={menu.id}
                        className="d-flex align-items-center mb-1"
                        style={{ gap: "6px" }}
                      >
                        <input
                          type="checkbox"
                          id={`menu-${menu.id}`} // associate label with this input
                          style={{ fontSize: 11 }}
                          className="form-check-input checkbox-sm"
                          checked={isChecked}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCheckedMenus((prev) => [...prev, menu.id]);
                            } else {
                              setCheckedMenus((prev) => prev.filter((id) => id !== menu.id));
                            }
                          }}
                        />
                        <label
                          htmlFor={`menu-${menu.id}`} // this makes clicking the text toggle the checkbox
                          className="form-check-label"
                          onClick={(e) => e.stopPropagation()}
                          style={{ cursor: "pointer" }} // show pointer cursor
                        >
                          {menu?.title}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUserModal(false)}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" onClick={handleSaveUser}>
            {editingUser ? t("Update") : t("Add User")}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("Confirm Delete")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {userToDelete && (
            <p>
              {t("Are you sure you want to delete user")}{" "}
              <strong>{userToDelete.name}</strong> ({userToDelete.email}) ?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
             {t("Cancel")}
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            {t("Delete")}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Access Modal */}
      <Modal
        show={showAccessModal}
        onHide={() => setShowAccessModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("Dashboard Access & Permissions")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingUser && (
            <>
              <p className="mb-3">
                {t("Assign access for:")} <strong>{editingUser.name}</strong>
              </p>
              <div>
                {allPermission &&
                allPermission.map((menu) => {
                  const isChecked = checkedMenus.includes(menu.id);

                  return (
                    <div
                      key={menu.id}
                      className="d-flex align-items-center mb-1"
                      style={{ gap: "6px" }}
                    >
                      <input
                        type="checkbox"
                        id={`menu-${menu.id}`} // associate label with this input
                        style={{ fontSize: 11 }}
                        className="form-check-input checkbox-sm"
                        checked={isChecked}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCheckedMenus((prev) => [...prev, menu.id]);
                          } else {
                            setCheckedMenus((prev) => prev.filter((id) => id !== menu.id));
                          }
                        }}
                      />
                      <label
                        htmlFor={`menu-${menu.id}`} // this makes clicking the text toggle the checkbox
                        className="form-check-label"
                        onClick={(e) => e.stopPropagation()}
                        style={{ cursor: "pointer" }} // show pointer cursor
                      >
                        {menu?.title}
                      </label>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAccessModal(false)}>
            {t("Cancel")}
          </Button>
          <Button variant="success" onClick={handleSaveAccess}>
            {t("Save Access")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAllWebsitesModal}
        onHide={() => setShowAllWebsitesModal(false)}
        centered
      >
        {" "}
        <Modal.Header closeButton>
          {" "}
          <Modal.Title>{t("All Permission")}</Modal.Title>{" "}
        </Modal.Header>{" "}
        <Modal.Body>
          {checkedMenus?.map((w: any, idx: number) => (
            <Badge
              bg="light"
              text="dark"
              className="me-1 mb-1 border custom-badge"
              key={idx}
            >
              {allPermission.find((d:any) => d.id === w)?.name}
            </Badge>
          ))}
        </Modal.Body>{" "}
      </Modal>
      
    </>
  )
}

export default AdminManagementPage;
